import { useState, useEffect, useCallback, useRef } from 'react';
import { directSupabase } from '../lib/supabaseClient';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import type { Database } from '../types/supabase';
import { useNavigate } from 'react-router-dom';

type Profile = Database['public']['Tables']['profiles']['Row'];

export function isSiteCrewRole(role?: string | null): boolean {
  return role === 'fitter' || role === 'labourer';
}

export function staffLandingPath(role?: string | null): string {
  if (isSiteCrewRole(role)) return '/job-records';
  if (role === 'supplier') return '/supplier-portal';
  if (role === 'document') return '/docs';
  return '/dashboard';
}

export function isPathAllowedForRole(role: string | null | undefined, pathname: string): boolean {
  if (isSiteCrewRole(role)) {
    return pathname === '/job-records'
      || pathname === '/cis-certificates'
      || (/^\/job-records\/[^/]+$/.test(pathname) && pathname !== '/job-records/review');
  }
  if (role === 'supplier') return pathname.startsWith('/supplier-portal');
  if (role === 'document') return pathname === '/docs' || pathname.startsWith('/docs/');
  return true;
}

export interface UseProfileResult {
  profile: Profile | null;
  loading: boolean;
  error: string | null;
  isStaff: boolean;
  isAdmin: boolean;
  isCustomer: boolean;
  isFitter: boolean;
  isLabourer: boolean;
  isSiteCrew: boolean;
  isSupplier: boolean;
  canAccessAdmin: boolean;
  canAccessApp: boolean;
  hasDocumentAccess: boolean;
  refreshProfile: () => Promise<void>;
}

export function useProfile(): UseProfileResult {
  const { user, signOut } = useSupabaseAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const signOutRef = useRef(false);
  const profileLoadedRef = useRef(false);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      profileLoadedRef.current = false;
      return;
    }

    // Prevent multiple calls for the same user
    if (profileLoadedRef.current) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error } = await directSupabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        setError('Failed to fetch profile');
        if (error.message && error.message.toLowerCase().includes('jwt') && !signOutRef.current) {
          signOutRef.current = true;
          await signOut();
          navigate('/login', { replace: true, state: { reason: 'session-expired' } });
        }
      } else {
        setProfile(data);
        profileLoadedRef.current = true;
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch profile');
      if (err instanceof Error && err.message.toLowerCase().includes('jwt') && !signOutRef.current) {
        signOutRef.current = true;
        await signOut();
        navigate('/login', { replace: true, state: { reason: 'session-expired' } });
      }
    } finally {
      setLoading(false);
    }
  }, [user, signOut, navigate]);

  const createProfile = async (profileData: any) => {
    if (!user) {
      setError('User ID is required');
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error } = await directSupabase
        .from('profiles')
        .insert([profileData])
        .select()
        .single();
      
      if (error) {
        setError('Failed to create profile');
        return null;
      } else {
        setProfile(data);
        profileLoadedRef.current = true;
        return data;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create profile');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: any) => {
    if (!user) {
      setError('User ID is required');
      return null;
    }

    try {
      setLoading(true);
      setError(null);
      const { data, error } = await directSupabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();
      
      if (error) {
        setError('Failed to update profile');
        return null;
      } else {
        setProfile(data);
        profileLoadedRef.current = true;
        return data;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Reset the loaded flag when user changes
    profileLoadedRef.current = false;
    fetchProfile();
  }, [user?.id]); // Only depend on user ID, not the entire user object

  const isStaff = profile?.group === 'staff';
  const isAdmin = isStaff && profile?.role === 'admin';
  const isFitter = isStaff && profile?.role === 'fitter';
  const isLabourer = isStaff && profile?.role === 'labourer';
  const isSiteCrew = isFitter || isLabourer;
  const isSupplier = isStaff && profile?.role === 'supplier';
  const isCustomer = profile?.group === 'customer';
  const canAccessAdmin = isAdmin;
  const canAccessApp = isStaff; // Only staff can access the app for now
  const hasDocumentAccess = isAdmin || profile?.role === 'document';

  return {
    profile,
    loading,
    error,
    isStaff,
    isAdmin,
    isCustomer,
    isFitter,
    isLabourer,
    isSiteCrew,
    isSupplier,
    canAccessAdmin,
    canAccessApp,
    hasDocumentAccess,
    refreshProfile: fetchProfile
  };
} 